// Global tool-comparison tray. Stores up to 4 selected tool slugs in a React
// context + sessionStorage, so users can pick tools from anywhere in the app
// (ToolCard checkbox, /compare dropdowns) and view them side-by-side at
// /compare. Slugs are the canonical identifier (matches `tools.slug` in DB
// and `slugify(tool.name)` for the legacy hardcoded list).
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "sonner";

const STORAGE_KEY = "hl_compare_tray";
export const COMPARE_MAX = 4;

interface CompareTrayContextValue {
  slugs: string[];
  has: (slug: string) => boolean;
  toggle: (slug: string, displayName?: string) => void;
  add: (slug: string, displayName?: string) => void;
  remove: (slug: string) => void;
  clear: () => void;
  isFull: boolean;
}

const CompareTrayContext = createContext<CompareTrayContextValue | null>(null);

const readInitial = (): string[] => {
  if (typeof window === "undefined") return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((s) => typeof s === "string").slice(0, COMPARE_MAX) : [];
  } catch {
    return [];
  }
};

export const CompareTrayProvider = ({ children }: { children: ReactNode }) => {
  const [slugs, setSlugs] = useState<string[]>(() => readInitial());

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(slugs));
    } catch {
      /* ignore quota errors */
    }
  }, [slugs]);

  const has = useCallback((slug: string) => slugs.includes(slug), [slugs]);

  const add = useCallback((slug: string, displayName?: string) => {
    setSlugs((prev) => {
      if (prev.includes(slug)) return prev;
      if (prev.length >= COMPARE_MAX) {
        toast.error(`You can compare up to ${COMPARE_MAX} tools at once`);
        return prev;
      }
      toast.success(`Added ${displayName || slug} to compare`);
      return [...prev, slug];
    });
  }, []);

  const remove = useCallback((slug: string) => {
    setSlugs((prev) => prev.filter((s) => s !== slug));
  }, []);

  const toggle = useCallback((slug: string, displayName?: string) => {
    setSlugs((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= COMPARE_MAX) {
        toast.error(`You can compare up to ${COMPARE_MAX} tools at once`);
        return prev;
      }
      toast.success(`Added ${displayName || slug} to compare`);
      return [...prev, slug];
    });
  }, []);

  const clear = useCallback(() => setSlugs([]), []);

  const value = useMemo<CompareTrayContextValue>(
    () => ({ slugs, has, toggle, add, remove, clear, isFull: slugs.length >= COMPARE_MAX }),
    [slugs, has, toggle, add, remove, clear]
  );

  return <CompareTrayContext.Provider value={value}>{children}</CompareTrayContext.Provider>;
};

export const useCompareTray = () => {
  const ctx = useContext(CompareTrayContext);
  if (!ctx) throw new Error("useCompareTray must be used within CompareTrayProvider");
  return ctx;
};
